import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { bidService } from '../services/bidService';
import { Bid } from '../models/bid';
import {
  ArrowLeft,
  Loader2,
  FileText,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileCheck,
  AlertCircle,
  Building,
  Hash,
  Clock,
  CheckCircle,
  FileSignature,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BarChart3,
  XCircle,
  Info,
  Home,
  Shield,
  ShieldCheck,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Download,
  Upload,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { LineItemTable } from '../components/BidAnalysis';
import { ArchDocExtraction } from '../components/ArchDocExtraction';

export const BidDetailPage: React.FC = () => {
  const { bidId } = useParams<{ bidId: string }>();
  const navigate = useNavigate();
  const [bid, setBid] = useState<Bid | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(true);
  const [contractorVerificationExpanded, setContractorVerificationExpanded] = useState(false);
  const [feasibilityExpanded, setFeasibilityExpanded] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [contractorVerificationDetailsExpanded, setContractorVerificationDetailsExpanded] = useState(false);
  const [propertyVerificationDetailsExpanded, setPropertyVerificationDetailsExpanded] = useState(false);
  const [archDocUploading, setArchDocUploading] = useState(false);
  const [archDocStatus, setArchDocStatus] = useState<string | null>(null);
  const archDocInputRef = React.useRef<HTMLInputElement>(null);

  // Aggregate line items by houseLocation for "Scope of Work" summary
  const workAreaSummary = useMemo(() => {
    const lineItems = bid?.extractedData?.lineItems;
    if (!lineItems) return [];
    const areas: Record<string, { name: string; totalCost: number; itemCount: number; jobTypes: Set<string> }> = {};
    for (const item of lineItems) {
      const loc = item.houseLocation || 'Unspecified';
      if (!areas[loc]) areas[loc] = { name: loc, totalCost: 0, itemCount: 0, jobTypes: new Set() };
      areas[loc].totalCost += item.totalCost || 0;
      areas[loc].itemCount += 1;
      if (item.jobType) areas[loc].jobTypes.add(item.jobType);
    }
    return Object.values(areas)
      .map(a => ({ ...a, jobTypes: Array.from(a.jobTypes) }))
      .sort((a, b) => b.totalCost - a.totalCost);
  }, [bid]);

  useEffect(() => {
    if (bidId) {
      loadBid(bidId);
    }
  }, [bidId]);

  const loadBid = async (id: string) => {
    try {
      setLoading(true);
      const bidData = await bidService.getBid(id);
      setBid(bidData);

      // If still processing, start polling
      // Status flow: processing → raw_extracted → formatted → analyzing → analyzed
      const activeStatuses = ['processing', 'raw_extracted', 'formatted', 'analyzing'];
      if (activeStatuses.includes(bidData.status) && !isPolling) {
        setIsPolling(true);
        pollForCompletion(id);
      }
    } catch (err) {
      console.error('Error loading bid:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bid');
    } finally {
      setLoading(false);
    }
  };

  const pollForCompletion = async (id: string) => {
    try {
      const completedBid = await bidService.pollBidStatus(id, (updatedBid) => {
        setBid(updatedBid);
      });
      setBid(completedBid);
    } catch (err) {
      console.error('Error polling bid status:', err);
      setError('Failed to complete extraction');
    } finally {
      setIsPolling(false);
    }
  };

  const handleViewDocument = async () => {
    if (!bidId) return;

    try {
      const documentUrl = await bidService.getBidDocument(bidId);
      window.open(documentUrl, '_blank');
    } catch (err) {
      console.error('Error fetching document URL:', err);
      setError('Failed to load document');
    }
  };

  const handleAnalyzeBid = async () => {
    if (!bidId) return;

    try {
      setIsPolling(true);
      await bidService.analyzeBid(bidId);

      // Poll in background without updating UI constantly
      // Just check every few seconds until analysis is done
      const checkAnalysis = setInterval(async () => {
        try {
          const updatedBid = await bidService.getBid(bidId);
          if (updatedBid.status === 'analyzed' || updatedBid.status === 'failed') {
            clearInterval(checkAnalysis);
            setBid(updatedBid);
            setIsPolling(false);
          }
        } catch (err) {
          clearInterval(checkAnalysis);
          setIsPolling(false);
          console.error('Error checking analysis status:', err);
        }
      }, 3000);
    } catch (err) {
      console.error('Error analyzing bid:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze bid');
      setIsPolling(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!bidId) return;

    // Check if analysis is complete
    if (bid?.status !== 'analyzed') {
      setError('Analysis must complete before generating report');
      return;
    }

    try {
      setIsDownloadingReport(true);
      const result = await bidService.getBidReport(bidId);

      // Open the report URL in a new tab
      window.open(result.reportUrl, '_blank');
    } catch (err) {
      console.error('Error downloading report:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleUploadArchDoc = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !bidId) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    try {
      setArchDocUploading(true);
      setArchDocStatus('Uploading architectural document...');

      // Step 1: Upload the architectural document
      const uploadResult = await bidService.uploadArchitecturalDocument(file);
      const archDocId = uploadResult.architecturalDocument.id;

      // Step 2: Poll until extraction completes
      setArchDocStatus('Extracting measurements from plans...');
      const extractedDoc = await bidService.pollArchDocStatus(archDocId, (doc) => {
        setArchDocStatus(doc.statusMessage || 'Processing...');
      });

      if (extractedDoc.status === 'failed') {
        setError('Architectural document extraction failed');
        setArchDocUploading(false);
        setArchDocStatus(null);
        return;
      }

      // Step 3: Link to bid (triggers re-analysis)
      setArchDocStatus('Linking to bid and re-analyzing...');
      await bidService.linkArchitecturalDocument(bidId, archDocId);

      // Step 4: Poll bid until re-analysis completes
      setIsPolling(true);
      setArchDocStatus(null);
      setArchDocUploading(false);

      const checkReanalysis = setInterval(async () => {
        try {
          const updatedBid = await bidService.getBid(bidId);
          setBid(updatedBid);
          if (updatedBid.status === 'analyzed' || updatedBid.status === 'failed') {
            clearInterval(checkReanalysis);
            setIsPolling(false);
          }
        } catch (err) {
          clearInterval(checkReanalysis);
          setIsPolling(false);
        }
      }, 3000);

    } catch (err) {
      console.error('Error uploading architectural document:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload architectural document');
      setArchDocUploading(false);
      setArchDocStatus(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'analyzed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'extracted':
      case 'formatted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'analyzing':
      case 'processing':
      case 'raw_extracted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Helper to check if data exists and has content
  const hasData = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // Get contractor verification status display
  const getVerificationStatus = (verification: any) => {
    if (!verification) return null;

    const hasRedFlags = verification.redFlags && verification.redFlags.length > 0;
    if (hasRedFlags) {
      return {
        text: 'Verification Failed',
        icon: ShieldAlert,
        className: 'text-red-600 bg-red-50 border-red-200',
        badgeClassName: 'bg-red-100 text-red-800 border-red-200'
      };
    }

    if (verification.status === 'verified') {
      return {
        text: 'Verified',
        icon: ShieldCheck,
        className: 'text-green-600 bg-green-50 border-green-200',
        badgeClassName: 'bg-green-100 text-green-800 border-green-200'
      };
    }

    if (verification.status === 'partial_match') {
      return {
        text: 'Business Verified',
        icon: ShieldCheck,
        className: 'text-green-600 bg-green-50 border-green-200',
        badgeClassName: 'bg-green-100 text-green-800 border-green-200'
      };
    }

    return {
      text: 'Pending Verification',
      icon: Clock,
      className: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      badgeClassName: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mb-4" />
          <p className="text-gray-500">Loading bid details...</p>
        </div>
      </div>
    );
  }

  if (error || !bid) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">Error Loading Bid</h3>
            <p className="text-gray-500 mb-6">{error || 'Bid not found'}</p>
            <Button onClick={() => navigate('/bids')}>Back to Bids</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { extractedData } = bid;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/bids')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Bids
            </Button>
            <div>
              <p className="text-sm text-gray-500">
                Uploaded {new Date(bid.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleViewDocument}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View Original PDF
            </Button>
            {bid.status === 'analyzed' && (
              <Button
                variant="outline"
                onClick={handleDownloadReport}
                disabled={isDownloadingReport}
                className="gap-2"
              >
                {isDownloadingReport ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download Report
                  </>
                )}
              </Button>
            )}
            {bid.status === 'analyzed' && (
              <>
                <input
                  ref={archDocInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleUploadArchDoc}
                />
                <Button
                  variant="outline"
                  onClick={() => archDocInputRef.current?.click()}
                  disabled={archDocUploading || isPolling}
                  className="gap-2"
                >
                  {archDocUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {archDocStatus || 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      {bid.architecturalDocumentId ? 'Replace Plans' : 'Upload Plans'}
                    </>
                  )}
                </Button>
              </>
            )}
            {(bid.status === 'extracted' || bid.status === 'formatted') && !bid.structuralAnalysis && (
              <Button
                onClick={handleAnalyzeBid}
                disabled={isPolling}
                className="gap-2 bg-fxer-blue hover:opacity-90"
              >
                {isPolling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-4 w-4" />
                    Analyze Bid
                  </>
                )}
              </Button>
            )}
            <Badge className={getStatusBadgeClass(bid.status)}>
              {(bid.status === 'processing' || bid.status === 'raw_extracted' || bid.status === 'analyzing') && (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              )}
              {(bid.status === 'extracted' || bid.status === 'formatted' || bid.status === 'analyzed') && (
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
              )}
              {bid.status === 'failed' && (
                <AlertCircle className="h-3.5 w-3.5 mr-1" />
              )}
              {bid.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>

        {/* Failed State */}
        {bid.status === 'failed' && (
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">Extraction Failed</p>
              <p className="text-sm text-gray-600">{bid.error}</p>
            </CardContent>
          </Card>
        )}

        {/* Extracted Data - Summary and Info Cards */}
        {(bid.status === 'extracted' || bid.status === 'formatted' || bid.status === 'analyzed' || bid.status === 'analyzing' || bid.status === 'raw_extracted') && extractedData && (
          <>
            {/* Summary Header - Simple */}
            {extractedData.summary && (
              <div className="py-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div className="flex-1">
                    <h2 className="text-sm font-medium text-gray-600 mb-1">Total Bid Amount</h2>
                    <div className="text-4xl font-bold text-fxer-blue">
                      {formatCurrency(
                        (extractedData.summary as any).totalBidAmount ||
                        extractedData.summary.total ||
                        (extractedData.lineItems ? extractedData.lineItems.reduce((sum, item) => sum + (item.totalCost || 0), 0) : null)
                      )}
                    </div>
                    {(() => {
                      const fxerMidpoint = bid.costAnalysis?.totalEstimateLikely || (
                        bid.costAnalysis?.totalEstimateLow && bid.costAnalysis?.totalEstimateHigh
                          ? Math.round((bid.costAnalysis.totalEstimateLow + bid.costAnalysis.totalEstimateHigh) / 2)
                          : null
                      );
                      if (!fxerMidpoint || fxerMidpoint <= 0) return null;
                      const bidTotal = (extractedData.summary as any).totalBidAmount ||
                        extractedData.summary.total ||
                        (extractedData.lineItems ? extractedData.lineItems.reduce((sum: number, item: any) => sum + (item.totalCost || 0), 0) : 0);
                      const diff = bidTotal && fxerMidpoint ? Math.round(((bidTotal - fxerMidpoint) / fxerMidpoint) * 100) : 0;
                      const diffColor = Math.abs(diff) <= 10 ? 'text-green-600' : diff < 0 ? 'text-amber-600' : 'text-red-600';
                      return (
                        <p className="text-sm text-gray-500 mt-1">
                          Fxer Estimate: <span className="font-medium text-gray-700">{formatCurrency(fxerMidpoint)}</span>
                          <span className={`ml-2 font-medium ${diffColor}`}>
                            ({diff >= 0 ? '+' : ''}{diff}%)
                          </span>
                        </p>
                      );
                    })()}
                  </div>

                  <div className="flex flex-wrap gap-6 text-sm">
                    {hasData(extractedData.summary.estimatedDurationDays) && (
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Duration</p>
                        <p className="font-medium text-gray-900">{extractedData.summary.estimatedDurationDays} days</p>
                      </div>
                    )}
                    {hasData((extractedData.summary as any).documentCompleteness) && (
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Completeness</p>
                        <p className="font-medium text-gray-900 capitalize">{(extractedData.summary as any).documentCompleteness}</p>
                      </div>
                    )}
                    {((extractedData.summary as any)?.hasLaborMaterialSplit ||
                      extractedData.documentMetadata?.hasLaborMaterialSplit) && (
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Cost Breakdown</p>
                        <p className="font-medium text-gray-900">Labor & Materials</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contractor, Client & Project Info - 3 Card Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Contractor Card */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3" style={{ color: 'rgb(19, 30, 58)' }}>Contractor</h3>
                  <div className="space-y-3">
                    {hasData(extractedData.contractor?.company) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Company</div>
                        <div className="text-sm font-medium text-gray-800">
                          {extractedData.contractor.company}
                          {bid.contractorVerification?.matches?.nameMatch && ' ✓'}
                        </div>
                      </div>
                    )}
                    {hasData(extractedData.contractor?.name) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Name</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.contractor.name}</div>
                      </div>
                    )}
                    {hasData(extractedData.contractor?.phone) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Phone</div>
                        <div className="text-sm font-medium text-gray-800">
                          {extractedData.contractor.phone}
                          {bid.contractorVerification?.matches?.phoneMatch && ' ✓'}
                        </div>
                      </div>
                    )}
                    {hasData(extractedData.contractor?.email) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Email</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.contractor.email}</div>
                      </div>
                    )}
                    {hasData(extractedData.contractor?.licenseNumber) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">License</div>
                        <div className="text-sm font-medium text-gray-800">
                          {extractedData.contractor.licenseNumber}
                          {bid.contractorVerification?.matches?.licenseMatch && ' ✓'}
                        </div>
                      </div>
                    )}
                    {bid.contractorVerification?.foundInfo?.website && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Website</div>
                        <a
                          href={bid.contractorVerification.foundInfo.website.startsWith('http')
                            ? bid.contractorVerification.foundInfo.website
                            : `https://${bid.contractorVerification.foundInfo.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline break-words"
                        >
                          {bid.contractorVerification.foundInfo.website}
                        </a>
                      </div>
                    )}

                    {/* Verification Status */}
                    {bid.contractorVerification && (
                      <div className="pt-3 mt-3 border-t border-gray-200">
                        <div className="mb-2 cursor-pointer" onClick={() => setContractorVerificationDetailsExpanded(!contractorVerificationDetailsExpanded)}>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500 mb-0.5">Verification Status</div>
                            {contractorVerificationDetailsExpanded ? (
                              <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                            )}
                          </div>
                          <div className={`text-sm font-semibold ${
                            bid.contractorVerification.status === 'verified' ? 'text-green-600' :
                            bid.contractorVerification.status === 'partial_match' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {bid.contractorVerification.status === 'verified' ? '✓ Verified' :
                             bid.contractorVerification.status === 'partial_match' ? '✓ Business Verified' :
                             '⚠ Pending Verification'}
                          </div>
                        </div>

                        {/* Verification Details */}
                        {contractorVerificationDetailsExpanded && (
                          <>
                            <div className="mt-2 space-y-1">
                              <div className="text-[7pt] uppercase text-gray-700" style={{ letterSpacing: '0.5px' }}>Verification Details</div>
                              <div className="space-y-0.5" style={{ fontSize: '8pt' }}>
                                <div className="flex items-center gap-1.5">
                                  <span className={bid.contractorVerification.matches.nameMatch ? 'text-green-600' : 'text-red-600'}>
                                    {bid.contractorVerification.matches.nameMatch ? '✓' : '✗'}
                                  </span>
                                  <span className="text-gray-700">Business name {bid.contractorVerification.matches.nameMatch ? 'verified' : 'pending'}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className={bid.contractorVerification.matches.phoneMatch ? 'text-green-600' : 'text-red-600'}>
                                    {bid.contractorVerification.matches.phoneMatch ? '✓' : '✗'}
                                  </span>
                                  <span className="text-gray-700">Phone number {bid.contractorVerification.matches.phoneMatch ? 'verified' : 'pending'}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className={bid.contractorVerification.matches.licenseMatch ? 'text-green-600' : 'text-red-600'}>
                                    {bid.contractorVerification.matches.licenseMatch ? '✓' : '✗'}
                                  </span>
                                  <span className="text-gray-700">License {bid.contractorVerification.matches.licenseMatch ? 'verified' : 'pending'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Red Flags */}
                            {bid.contractorVerification.redFlags && bid.contractorVerification.redFlags.length > 0 && (
                              <div className="mt-3 bg-red-50 border border-red-200 rounded p-2">
                                <div className="text-[7pt] uppercase text-red-900 font-semibold mb-1.5">Red Flags</div>
                                <div className="space-y-1" style={{ fontSize: '7pt' }}>
                                  {bid.contractorVerification.redFlags.map((flag, idx) => (
                                    <div key={idx} className="flex items-start gap-1.5 text-red-900">
                                      <span className="mt-0.5">⚠</span>
                                      <span>{flag}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Verification Sources */}
                            {bid.contractorVerification.sources && bid.contractorVerification.sources.length > 0 && (
                              <div className="mt-3">
                                <div className="text-[7pt] uppercase text-gray-700 font-semibold mb-1">Verification Sources</div>
                                <div className="space-y-0.5" style={{ fontSize: '7pt' }}>
                                  {bid.contractorVerification.sources.map((source, idx) => (
                                    <div key={idx} className="flex items-start gap-1.5 text-gray-600">
                                      <span>•</span>
                                      <span>{source}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Client Card */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3" style={{ color: 'rgb(19, 30, 58)' }}>Client</h3>
                  <div className="space-y-3">
                    {hasData(extractedData.client?.name) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Name</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.client.name}</div>
                      </div>
                    )}
                    {hasData(extractedData.client?.phone) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Phone</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.client.phone}</div>
                      </div>
                    )}
                    {hasData(extractedData.client?.email) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Email</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.client.email}</div>
                      </div>
                    )}
                    {hasData(extractedData.client?.address) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Address</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.client.address}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Project & Property Card */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3" style={{ color: 'rgb(19, 30, 58)' }}>Project & Property</h3>
                  <div className="space-y-3">
                    {hasData(extractedData.project?.projectAddress) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Address</div>
                        <div className="text-sm font-medium text-gray-800">
                          {extractedData.project.projectAddress}
                          {bid?.propertyData && ' ✓'}
                        </div>
                      </div>
                    )}
                    {hasData(extractedData.project?.bidDate) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Bid Date</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.project.bidDate}</div>
                      </div>
                    )}
                    {hasData(extractedData.project?.expirationDate) && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Expiration Date</div>
                        <div className="text-sm font-medium text-gray-800">{extractedData.project.expirationDate}</div>
                      </div>
                    )}
                    {bid?.propertyData?.bedrooms && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Bedrooms</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.bedrooms} bed</div>
                      </div>
                    )}
                    {bid?.propertyData?.bathrooms && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Bathrooms</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.bathrooms} bath</div>
                      </div>
                    )}
                    {bid?.propertyData?.squareFootage && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Square Footage</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.squareFootage.toLocaleString()} sq ft</div>
                      </div>
                    )}
                    {bid?.propertyData?.lotSizeSqFt && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Lot Size</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.lotSizeSqFt.toLocaleString()} sq ft</div>
                      </div>
                    )}
                    {bid?.propertyData?.yearBuilt && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Year Built</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.yearBuilt}</div>
                      </div>
                    )}
                    {bid?.propertyData?.propertyType && (
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Property Type</div>
                        <div className="text-sm font-medium text-gray-800">{bid.propertyData.propertyType}</div>
                      </div>
                    )}

                    {/* Property Verification */}
                    {bid?.propertyData && (
                      <div className="pt-3 mt-3 border-t border-gray-200">
                        <div className="mb-2 cursor-pointer" onClick={() => setPropertyVerificationDetailsExpanded(!propertyVerificationDetailsExpanded)}>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500 mb-0.5">Property Verification</div>
                            {propertyVerificationDetailsExpanded ? (
                              <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                            )}
                          </div>
                          <div className={`text-sm font-semibold ${
                            bid.propertyData.source === 'datafiniti' ? 'text-green-600' :
                            bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'high' ? 'text-green-600' :
                            bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'medium' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {bid.propertyData.source === 'datafiniti' ? '✓ Verified' :
                             bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'high' ? '✓ Verified' :
                             bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'medium' ? '✓ Verified' :
                             bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'low' ? '⏱ Low Confidence' :
                             '⚠ Estimated'}
                          </div>
                        </div>
                        {propertyVerificationDetailsExpanded && (
                          <>
                            <div className="text-gray-600" style={{ fontSize: '8pt' }}>
                              {bid.propertyData.source === 'datafiniti' ? 'Verified via DataInfiniti' :
                               bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'high' ? 'High Confidence' :
                               bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'medium' ? 'Medium Confidence' :
                               bid.propertyData.source === 'estimated_from_web_research' && bid.propertyData.confidence === 'low' ? 'Low Confidence' :
                               'Fallback Estimates'}
                            </div>
                            {bid.propertyData.researchSources && bid.propertyData.researchSources.length > 0 && (
                              <div className="mt-2">
                                <div className="text-[7pt] uppercase text-gray-700 font-semibold mb-1">Research Sources</div>
                                <div className="space-y-0.5" style={{ fontSize: '7pt' }}>
                                  {bid.propertyData.researchSources.map((source: string, idx: number) => (
                                    <div key={idx} className="flex items-start gap-1.5 text-gray-600">
                                      <span>•</span>
                                      <span>{source}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Processing State - Show after info cards */}
        {(bid.status === 'processing' || bid.status === 'raw_extracted' || bid.status === 'formatted' || bid.status === 'analyzing') && (
          <Card className={bid.status === 'analyzing' ? 'border-blue-200 bg-blue-50' : ''}>
            <CardContent className="py-8 text-center">
              <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                {bid.statusMessage || 'Processing bid...'}
              </p>
              <p className="text-sm text-gray-600">
                The page will update automatically as processing continues.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Line Items and Details - Only show when analyzed */}
        {/* Architectural Document Extraction */}
        {bid.status === 'analyzed' && bid.architecturalDocumentId && (
          <ArchDocExtraction architecturalDocumentId={bid.architecturalDocumentId} />
        )}

        {/* Document Index — shows when classification detected multiple document types */}
        {(bid as any).documentClassification?.documents?.length > 1 && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Document Package
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-gray-500 mb-3">{(bid as any).documentClassification.summary}</p>
              <div className="space-y-1.5">
                {(bid as any).documentClassification.documents.map((doc: any, i: number) => {
                  const typeColors: Record<string, string> = {
                    bid: 'bg-blue-100 text-blue-700',
                    appraisal: 'bg-purple-100 text-purple-700',
                    architectural_plan: 'bg-green-100 text-green-700',
                    specifications: 'bg-amber-100 text-amber-700',
                    photos: 'bg-pink-100 text-pink-700',
                    cover_letter: 'bg-gray-100 text-gray-600',
                    other: 'bg-gray-100 text-gray-600',
                  };
                  const typeLabels: Record<string, string> = {
                    bid: 'Contractor Bid',
                    appraisal: 'Appraisal',
                    architectural_plan: 'Plans/Drawings',
                    specifications: 'Specifications',
                    photos: 'Photos',
                    cover_letter: 'Cover Letter',
                    other: 'Other',
                  };
                  const colorClass = typeColors[doc.type] || typeColors.other;
                  const label = typeLabels[doc.type] || doc.type;
                  const pages = doc.pageRange ? `pp. ${doc.pageRange.start}–${doc.pageRange.end}` : '';

                  return (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>{label}</span>
                      <span className="text-gray-600 flex-1">{doc.description}</span>
                      {pages && <span className="text-xs text-gray-400 flex-shrink-0">{pages}</span>}
                    </div>
                  );
                })}
              </div>

              {/* Appraisal gap summary if present */}
              {(bid as any).appraisalCrossReference?.gaps?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <span className="text-xs font-semibold text-gray-700">Appraisal Gaps ({(bid as any).appraisalCrossReference.gaps.length})</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{(bid as any).appraisalCrossReference.summary}</p>
                  <div className="space-y-1">
                    {(bid as any).appraisalCrossReference.gaps.map((gap: any, i: number) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                          gap.severity === 'critical' ? 'bg-red-500' : gap.severity === 'important' ? 'bg-amber-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <span className="font-medium text-gray-700">{gap.item}</span>
                          <span className="text-gray-500"> — {gap.explanation}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {bid.status === 'analyzed' && extractedData && (
          <>
            {/* Analysis Summary - Matches PDF Design */}
            <Card className="border-2 border-gray-300">
              <CardHeader
                className="cursor-pointer bg-gray-50"
                onClick={() => setContractorVerificationExpanded(!contractorVerificationExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="font-semibold">Analysis</span>
                  {contractorVerificationExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </CardTitle>
              </CardHeader>
              {contractorVerificationExpanded && (
                <CardContent className="p-6 space-y-4">
                  {/* Analysis Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Commentary</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {bid.feasibilityReport?.globalChecklist && bid.feasibilityReport.globalChecklist.length > 0 ? (
                          bid.feasibilityReport.globalChecklist.map((item, index) => {
                            // Map status to badge style
                            const getStatusBadge = (status: string) => {
                              switch (status) {
                                case 'adequate':
                                  return <Badge className="bg-green-100 text-green-800 border-green-200">✓ Adequate</Badge>;
                                case 'attention_required':
                                  return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">⚠ Attention Required</Badge>;
                                case 'blocking':
                                  return <Badge className="bg-red-100 text-red-800 border-red-200">⚠ Blocking</Badge>;
                                case 'not_provided':
                                default:
                                  return <Badge className="bg-gray-100 text-gray-600 border-gray-200">— Not Provided</Badge>;
                              }
                            };

                            return (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.category}</td>
                                <td className="px-4 py-3 text-center">
                                  {getStatusBadge(item.status)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{item.commentary || 'No additional notes'}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                              No analysis data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Critical Issues */}
                  {bid.feasibilityReport?.criticalIssues && bid.feasibilityReport.criticalIssues.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Critical Issues</h4>
                      <ol className="list-decimal list-inside space-y-2">
                        {bid.feasibilityReport.criticalIssues.map((issue, idx) => (
                          <li key={idx} className="text-sm text-gray-700 pl-2">
                            {issue}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Contract Terms Detail */}
                  {bid.feasibilityReport?.contractTerms && Object.keys(bid.feasibilityReport.contractTerms).length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Contract Terms</h4>
                      <div className="space-y-2 text-sm">
                        {bid.feasibilityReport.contractTerms.paymentSchedule?.found && (
                          <div>
                            <span className="font-medium text-gray-700">Payment:</span>
                            <span className="text-gray-600 ml-2">
                              {[
                                bid.feasibilityReport.contractTerms.paymentSchedule.deposit,
                                bid.feasibilityReport.contractTerms.paymentSchedule.milestones,
                                bid.feasibilityReport.contractTerms.paymentSchedule.finalPayment
                              ].filter(Boolean).join(' | ') || 'Details not specified'}
                            </span>
                          </div>
                        )}
                        {bid.feasibilityReport.contractTerms.warranty?.found && (
                          <div>
                            <span className="font-medium text-gray-700">Warranty:</span>
                            <span className="text-gray-600 ml-2">
                              {[
                                bid.feasibilityReport.contractTerms.warranty.duration,
                                bid.feasibilityReport.contractTerms.warranty.coverage
                              ].filter(Boolean).join(' - ') || 'Details not specified'}
                            </span>
                          </div>
                        )}
                        {bid.feasibilityReport.contractTerms.timeline?.found && (
                          <div>
                            <span className="font-medium text-gray-700">Timeline:</span>
                            <span className="text-gray-600 ml-2">
                              {[
                                bid.feasibilityReport.contractTerms.timeline.startDate && `Start: ${bid.feasibilityReport.contractTerms.timeline.startDate}`,
                                bid.feasibilityReport.contractTerms.timeline.completionDate && `Complete: ${bid.feasibilityReport.contractTerms.timeline.completionDate}`,
                                bid.feasibilityReport.contractTerms.timeline.duration && `Duration: ${bid.feasibilityReport.contractTerms.timeline.duration}`
                              ].filter(Boolean).join(' | ') || 'Details not specified'}
                            </span>
                          </div>
                        )}
                        {bid.feasibilityReport.contractTerms.permits?.found && (
                          <div>
                            <span className="font-medium text-gray-700">Permits:</span>
                            <span className="text-gray-600 ml-2">
                              {[
                                bid.feasibilityReport.contractTerms.permits.responsibility,
                                bid.feasibilityReport.contractTerms.permits.specifics
                              ].filter(Boolean).join(' - ') || bid.feasibilityReport.contractTerms.permits.rawText || 'Details not specified'}
                            </span>
                          </div>
                        )}
                        {bid.feasibilityReport.contractTerms.insurance?.found && (
                          <div>
                            <span className="font-medium text-gray-700">Insurance:</span>
                            <span className="text-gray-600 ml-2">
                              {bid.feasibilityReport.contractTerms.insurance.liabilityCoverage || 'Specified'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Feasibility Report - Show First */}
            {bid.feasibilityReport && (
              <Card className={`border-2 ${
                bid.feasibilityReport.overallAssessment === 'proceed' ? 'border-green-300 bg-green-50/30' :
                bid.feasibilityReport.overallAssessment === 'negotiate' ? 'border-yellow-300 bg-yellow-50/30' :
                'border-red-300 bg-red-50/30'
              }`}>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setFeasibilityExpanded(!feasibilityExpanded)}
                >
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                      {bid.feasibilityReport.overallAssessment === 'proceed' ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : bid.feasibilityReport.overallAssessment === 'negotiate' ? (
                        <AlertCircle className="h-6 w-6 text-yellow-600" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-600" />
                      )}
                      <span>
                        Recommendation: <span className="capitalize font-bold">{bid.feasibilityReport.overallAssessment}</span>
                      </span>
                    </div>
                    {feasibilityExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </CardTitle>
                </CardHeader>
                {feasibilityExpanded && (
                  <CardContent className="space-y-4">
                  {/* Summary */}
                  <div>
                    <p className="text-base text-gray-900">{bid.feasibilityReport.summary}</p>
                  </div>

                  {/* Risk Indicators */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {bid.feasibilityReport.confidenceLevel && (
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Confidence Level</p>
                        <p className={`font-bold capitalize ${
                          bid.feasibilityReport.confidenceLevel === 'high' ? 'text-green-700' :
                          bid.feasibilityReport.confidenceLevel === 'medium' ? 'text-yellow-700' :
                          'text-red-700'
                        }`}>
                          {bid.feasibilityReport.confidenceLevel}
                        </p>
                      </div>
                    )}
                    {bid.feasibilityReport.scopeRisk && (
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Scope Risk</p>
                        <p className={`font-bold capitalize ${
                          bid.feasibilityReport.scopeRisk === 'clear' || bid.feasibilityReport.scopeRisk === 'minimal' ? 'text-green-700' :
                          bid.feasibilityReport.scopeRisk === 'moderate' || bid.feasibilityReport.scopeRisk === 'minor_gaps' ? 'text-yellow-700' :
                          'text-red-700'
                        }`}>
                          {bid.feasibilityReport.scopeRisk.replace(/_/g, ' ')}
                        </p>
                      </div>
                    )}
                    {bid.feasibilityReport.contractorRisk && (
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Contractor Risk</p>
                        <p className={`font-bold capitalize ${
                          bid.feasibilityReport.contractorRisk === 'low' || bid.feasibilityReport.contractorRisk === 'verified' ? 'text-green-700' :
                          bid.feasibilityReport.contractorRisk === 'partial' || bid.feasibilityReport.contractorRisk === 'moderate' ? 'text-yellow-700' :
                          'text-red-700'
                        }`}>
                          {bid.feasibilityReport.contractorRisk}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pricing Assessment */}
                  {bid.feasibilityReport.pricingAssessment && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Pricing Assessment</p>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="text-center">
                          <p className="text-xs text-gray-600">Overall</p>
                          <p className={`font-bold capitalize ${
                            bid.feasibilityReport.pricingAssessment.overall === 'fair' || bid.feasibilityReport.pricingAssessment.overall === 'reasonable' ? 'text-green-700' :
                            bid.feasibilityReport.pricingAssessment.overall === 'mixed' ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {bid.feasibilityReport.pricingAssessment.overall}
                          </p>
                        </div>
                        {bid.feasibilityReport.pricingAssessment.overpriced !== undefined && (
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Overpriced Items</p>
                            <p className="font-bold text-red-700">{bid.feasibilityReport.pricingAssessment.overpriced}</p>
                          </div>
                        )}
                        {bid.feasibilityReport.pricingAssessment.underpriced !== undefined && (
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Underpriced Items</p>
                            <p className="font-bold text-yellow-700">{bid.feasibilityReport.pricingAssessment.underpriced}</p>
                          </div>
                        )}
                      </div>
                      {bid.feasibilityReport.pricingAssessment.note && (
                        <p className="text-sm text-gray-700">{bid.feasibilityReport.pricingAssessment.note}</p>
                      )}
                    </div>
                  )}

                  {/* Red Flags */}
                  {bid.feasibilityReport.redFlags && bid.feasibilityReport.redFlags.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4" />
                        Red Flags
                      </p>
                      <ul className="space-y-2">
                        {bid.feasibilityReport.redFlags.map((flag: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-red-800">
                            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>{flag}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Required Actions */}
                  {bid.feasibilityReport.requiresAction && bid.feasibilityReport.requiresAction.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Required Actions Before Proceeding
                      </p>
                      <ul className="space-y-2">
                        {bid.feasibilityReport.requiresAction.map((action: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-blue-900">
                            <div className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0"></div>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  </CardContent>
                )}
              </Card>
            )}

            {/* Scope of Work - Work Areas Summary */}
            {workAreaSummary.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{ color: 'rgb(19, 30, 58)' }}>
                    <MapPin className="h-4 w-4 text-fxer-blue" />
                    Scope of Work
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {workAreaSummary.map((area) => (
                      <div
                        key={area.name}
                        className="border rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{area.name}</span>
                          <span className="text-sm font-semibold text-fxer-blue">
                            {formatCurrency(area.totalCost)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {area.itemCount} line item{area.itemCount !== 1 ? 's' : ''}
                        </div>
                        {area.jobTypes.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {area.jobTypes.map((jt: string) => (
                              <span
                                key={jt}
                                className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded"
                              >
                                {jt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Line Items */}
            {hasData(extractedData.lineItems) && extractedData.lineItems && extractedData.lineItems.length > 0 && (
              <div className="space-y-4">
                <button
                  onClick={() => setLineItemsExpanded(!lineItemsExpanded)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 hover:text-fxer-blue transition-colors">
                      {lineItemsExpanded ? (
                        <ChevronUp className="h-5 w-5 text-fxer-blue" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-fxer-blue" />
                      )}
                      <FileText className="h-5 w-5 text-fxer-blue" />
                      Line Items ({extractedData.lineItems.length})
                    </h3>
                    {!lineItemsExpanded && (
                      <div className="text-lg font-bold text-fxer-blue">
                        {formatCurrency(
                          extractedData.lineItems.reduce((sum, item) => sum + (item.totalCost || 0), 0)
                        )}
                      </div>
                    )}
                  </div>
                </button>
                {lineItemsExpanded && (
                  <LineItemTable
                    lineItems={extractedData.lineItems}
                    hasLaborMaterialSplit={
                      (extractedData.summary as any)?.hasLaborMaterialSplit ||
                      extractedData.documentMetadata?.hasLaborMaterialSplit ||
                      false
                    }
                    totalBidAmount={extractedData.summary?.totalBidAmount || extractedData.summary?.total}
                  />
                )}
              </div>
            )}

            {/* Payment Schedule */}
            {hasData(extractedData.summary?.paymentSchedule) && extractedData.summary?.paymentSchedule && extractedData.summary.paymentSchedule.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-fxer-blue" />
                  Payment Schedule
                </h3>
                <div className="space-y-2 pl-7">
                  {extractedData.summary.paymentSchedule.map((payment, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b last:border-b-0">
                      <span className="text-gray-700">{payment.trigger || 'Unspecified'}</span>
                      <span className="font-medium">
                        {payment.amount ? formatCurrency(payment.amount) : payment.percentage ? `${payment.percentage}%` : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Terms */}
            {(hasData(extractedData.summary?.warrantyTerms) ||
              hasData(extractedData.summary?.changeOrderPolicy) ||
              hasData(extractedData.summary?.notes) ||
              hasData(extractedData.summary?.includedMaterials) ||
              hasData(extractedData.summary?.excludedWork)) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileSignature className="h-5 w-5 text-fxer-blue" />
                  Additional Terms
                </h3>
                <div className="space-y-4 pl-7">
                  {hasData(extractedData.summary?.warrantyTerms) && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Warranty Terms</p>
                      <p className="text-sm text-gray-700">{extractedData.summary.warrantyTerms}</p>
                    </div>
                  )}
                  {hasData(extractedData.summary?.changeOrderPolicy) && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Change Order Policy</p>
                      <p className="text-sm text-gray-700">{extractedData.summary.changeOrderPolicy}</p>
                    </div>
                  )}
                  {hasData(extractedData.summary?.includedMaterials) && extractedData.summary.includedMaterials && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Included Materials</p>
                      <ul className="list-disc list-inside space-y-1">
                        {extractedData.summary.includedMaterials.map((material, idx) => (
                          <li key={idx} className="text-sm text-gray-700">{material}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasData(extractedData.summary?.excludedWork) && extractedData.summary.excludedWork && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Excluded Work</p>
                      <ul className="list-disc list-inside space-y-1">
                        {extractedData.summary.excludedWork.map((work, idx) => (
                          <li key={idx} className="text-sm text-gray-700">{work}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasData(extractedData.summary?.notes) && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{extractedData.summary.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
